import React, { FC, useMemo } from 'react';

import { EvMultiLineChart, EvSection } from '@evinced-private/ui-common';

import { createStrategicTickPositions } from 'src/common/helpers/chartHelpers';

import { ERROR_TYPE } from '../../types/chartErrors';
import { ErrorView } from '../error-view/ErrorView';
import { ChartSkeleton } from '../skeletons/chart-skeleton/ChartSkeleton';

import { prepareDataForAccessibilityChart } from './helpers/dataTransformHelpers';
import { createTableProps } from './helpers/tableHelpers';
import { tooltipFormatter } from './helpers/tooltipHelpers';
import { CHART_CONSTANTS, CHART_TEXT } from './constants';
import { AccessibilityTrendsChartProps } from './types';

import './AccessibilityTrendsChart.scss';

export const AccessibilityTrendsChart: FC<AccessibilityTrendsChartProps> = ({
	data,
	isLoading = false,
	error = null,
	title = CHART_TEXT.TITLE,
	description = CHART_TEXT.DESCRIPTION,
	height = CHART_CONSTANTS.HEIGHT,
	className = '',
	showTable = true
}) => {
	const chartData = useMemo(() => {
		if (!data || data.length === 0) {
			return { categories: [], series: [] };
		}
		return prepareDataForAccessibilityChart(data);
	}, [data]);

	const hasError = !!(error || !data || data.length === 0);
	const getErrorType = (): ERROR_TYPE => (error ? ERROR_TYPE.FAILED : ERROR_TYPE.NO_DATA);

	const chartClassName = `accessibility-trends-chart ${className}`.trim();

	if (isLoading) {
		return (
			<EvSection className={chartClassName}>
				<div
					className="accessibility-trends-chart-wrapper"
					data-automation-id="accessibility-trends-chart-wrapper"
				>
					<ChartSkeleton />
				</div>
			</EvSection>
		);
	}

	return (
		<EvSection className={chartClassName}>
			<div
				className="accessibility-trends-chart-wrapper"
				data-automation-id="accessibility-trends-chart-wrapper"
			>
				<EvMultiLineChart
					title={title}
					series={chartData.series}
					description={description}
					chartHeadingLevel={3}
					yAxisLabel="Score"
					height={height}
					showLegend={false}
					className="accessibility-trends-chart-inner"
					xAxis={{
						type: 'category' as const,
						categories: chartData.categories,
						tickPositions: createStrategicTickPositions(chartData.categories.length)
					}}
					yAxisAllowDecimals={false}
					tooltipOptions={{
						formatter: tooltipFormatter
					}}
					enableMarker={data?.length === 1}
					isError={hasError}
					renderErrorState={() => <ErrorView errorType={getErrorType()} />}
					{...(showTable && { tableProps: createTableProps() })}
				/>
			</div>
		</EvSection>
	);
};
